"use client"

import type { InputHTMLAttributes, Ref } from "react"
import { useSite } from "@/contexts/site/site.context"
import { DefaultInput } from "../components/DefaultInput/DefaultInput.component"

/**
 * A slightly modified regular expression which makes the top-level domain mandatory (e.g. it disallows `example@localhost`).
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input/email#basic_validation
 * @see https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
 * @see https://www.icann.org/en/announcements/details/new-gtld-dotless-domain-names-prohibited-30-8-2013-en
 */
// language=JSUnicodeRegexp
const rfc5322EmailRegex =
  "^[a-zA-Z0-9.!#$%&'*+\\/=?^_`\\{\\|\\}~\\-]+@[a-zA-Z0-9](?:[a-zA-Z0-9\\-]{0,61}[a-zA-Z0-9])?(?:\\.[a-zA-Z0-9](?:[a-zA-Z0-9\\-]{0,61}[a-zA-Z0-9])?)+$"

type EmailProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  "placeholder" | "type"
> & {
  readonly description?: string
  readonly onClear?: () => void
  readonly ref?: Ref<HTMLInputElement>
  readonly wrapperClassName?: string
}

export function Email({
  ref,
  wrapperClassName,
  ...defaultInputProps
}: EmailProps) {
  const { locale } = useSite()

  return (
    <DefaultInput
      autoComplete="email"
      placeholder={
        {
          da: "Email",
          de: "E-mail-Adresse",
          en: "E-mail address",
          no: "E-postadresse",
          sv: "E-postadress",
        }[locale]
      }
      ref={ref}
      wrapperClassName={wrapperClassName}
      // biome-ignore lint: allow passthrough props here
      {...defaultInputProps}
      data-testid="email"
      pattern={rfc5322EmailRegex}
      type="email"
    />
  )
}
